import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { PaperPlaneRight, EnvelopeSimple, WarningCircle } from "@phosphor-icons/react";

const TITLES = {
  booked: "Confirm booking email",
  reminder: "Confirm reminder email",
  completed: "Confirm completion email",
  on_my_way: "Confirm 'On my way' email",
};

// props: open, type, jobId, etaMinutes, onClose, onSent
export default function NotifyDialog({ open, type, jobId, etaMinutes, onClose, onSent }) {
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [message, setMessage] = useState("");
  const [info, setInfo] = useState(null);

  useEffect(() => {
    if (!open || !jobId || !type) return;
    setLoading(true); setMessage(""); setInfo(null);
    api.notifPreview({ type, job_id: jobId, eta_minutes: etaMinutes ?? null })
      .then((d) => { setInfo(d); setMessage(d.message); })
      .catch((e) => { toast.error(e.response?.data?.detail || "Could not build message"); onClose(); })
      .finally(() => setLoading(false));
  }, [open, jobId, type, etaMinutes]); // eslint-disable-line

  const send = async () => {
    if (!info?.has_email) return toast.error("Client has no email address");
    setSending(true);
    try {
      await api.notifSend({ type, job_id: jobId, eta_minutes: etaMinutes ?? null, message });
      toast.success("Email sent to " + (info.client_name || "customer"));
      onSent && onSent();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not send email");
    } finally { setSending(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="notify-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl flex items-center gap-2">
            <EnvelopeSimple size={20} weight="duotone" className="text-[#00B4D8]" /> {TITLES[type] || "Send email"}
          </DialogTitle>
          <DialogDescription>Review and edit before it emails to {info?.client_name || "the customer"}.</DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-8 text-center text-slate-400 text-sm">Preparing message…</div>
        ) : !info?.has_email ? (
          <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
            <WarningCircle size={18} className="mt-0.5 shrink-0" /> This customer doesn't have an email address on file. Add one to email them.
          </div>
        ) : (
          <div className="space-y-2">
            <div className="text-xs text-slate-500">To: <b className="text-slate-700">{info.to_email}</b></div>
            <textarea
              value={message} onChange={(e) => setMessage(e.target.value)} rows={5}
              data-testid="notify-message"
              className="w-full text-sm rounded-lg border border-slate-300 p-3 focus:outline-none focus:ring-2 focus:ring-[#00B4D8]/40 resize-none"
            />
            <div className="text-[11px] text-slate-400">{message.length} characters</div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={send} disabled={sending || loading || !info?.has_email} className="bg-[#00B4D8] hover:bg-cyan-600" data-testid="notify-send">
            <PaperPlaneRight size={16} weight="fill" className="mr-1.5" />{sending ? "Sending…" : "Send Email"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
